import { useState, useEffect } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'
import { getHostBookings, getHostNotifications, getHostActions, getHostAnalytics } from '../services/dataService'

/* ============ CoCafe Host — Dashboard ============ */
function ActionItem({ a, onResolve, go }) {
  const icons = { cancellation: <HI.alert s={17} />, review: <HI.star s={17} />, modify: <HI.refresh s={17} /> };
  const tone = { cancellation: "amber", review: "clay", modify: "blue" }[a.kind];
  return (
    <div className="row g12" style={{ padding: "14px 0", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
      <span style={{ color: `var(--${tone})`, marginTop: 1 }}>{icons[a.kind]}</span>
      <div className="grow">
        <div className="t-sm" style={{ fontWeight: 700 }}>{a.title}</div>
        <div className="t-xs muted" style={{ lineHeight: 1.5, marginTop: 2 }}>{a.detail}</div>
        <div className="t-xs muted" style={{ marginTop: 5 }}>{a.time}</div>
      </div>
      <div className="row g8" style={{ flexShrink: 0 }}>
        {a.kind === "cancellation" && <>
          <button className="btn btn-soft btn-sm" onClick={() => onResolve(a.id, "Refund declined")}>Decline</button>
          <button className="btn btn-pri btn-sm" onClick={() => onResolve(a.id, "Refund approved · " + money(a.amount))}>Approve refund</button>
        </>}
        {a.kind === "review" && <button className="btn btn-pri btn-sm" onClick={() => go("reviews")}>Respond</button>}
        {a.kind === "modify" && <>
          <button className="btn btn-soft btn-sm" onClick={() => onResolve(a.id, "Reschedule declined")}>Decline</button>
          <button className="btn btn-pri btn-sm" onClick={() => onResolve(a.id, "Booking rescheduled")}>Accept</button>
        </>}
      </div>
    </div>
  );
}

function TodaySchedule({ openBooking, bookings }) {
  const now = 11; // pretend current hour for the "now" line
  const items = bookings.filter(b => b.date === 'Today');
  return (
    <div className="col" style={{ gap: 0 }}>
      {items.map((b, i) => {
        const live = b.status === "checked-in";
        return (
          <div key={b.id} style={{ width: '100%' }}>
            {b.start > now && items[i - 1] && items[i - 1].start <= now && (
              <div style={{ padding: "2px 0 4px", width: '100%' }}>
                <span className="t-xs mono" style={{ color: "var(--clay)", fontWeight: 700 }}>NOW</span>
              </div>
            )}
            <button onClick={() => openBooking(b.id)}
              style={{ width: "100%", padding: "4px", borderRadius: "var(--r)", border: "1px solid var(--border)", background: "var(--surface)", marginBottom: 4, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Av name={b.guest} initials={b.initials} c={b.c} size="xs" />
              <div style={{ fontWeight: 600, fontSize: '11px', flex: 1, minWidth: 0 }}>{b.guest}</div>
              <div style={{ fontSize: '9px', flexShrink: 0 }}>
                {live ? <span className="badge green" style={{ padding: '1px 3px', fontSize: '8px' }}>In now</span> : <span className="t-xs muted">{H.hLabel(b.start)}</span>}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function InsightCard({ ins, go, toast }) {
  const Ico = HI[ins.icon] || HI.info;
  return (
    <div className="card card-pad" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: `var(--${ins.tone}-weak)`, color: `var(--${ins.tone})`, display: "grid", placeItems: "center", flexShrink: 0 }}>
        <Ico s={18} />
      </span>
      <div className="grow">
        <div className="t-h3">{ins.title}</div>
        <div className="t-sm muted" style={{ lineHeight: 1.5, marginTop: 3 }}>{ins.body}</div>
        {ins.action && <button className="btn btn-ghost btn-sm" style={{ marginTop: 11 }}
          onClick={() => { if (ins.action === "Review pricing") go("pricing"); else if (ins.action === "Create promo") go("pricing"); else toast(ins.action); }}>
          {ins.action} <HI.arrowR s={14} /></button>}
      </div>
    </div>
  );
}

function Dashboard({ go, toast, openBooking, actions, resolveAction, user }) {
  const [metric, setMetric] = useState("rev");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  // Get user-specific data
  const userBookings = getHostBookings(user.id);
  const userNotifications = getHostNotifications(user.id);
  const userActions = getHostActions(user.id);
  const analytics = getHostAnalytics(user.id);

  const todayRev = userBookings.reduce((s, b) => s + (b.date === 'Today' ? b.amount : 0), 0);
  const checkedIn = userBookings.filter((b) => b.status === "checked-in").length;
  const occ = analytics?.occupancy || 78;
  const metricCfg = {
    rev: { key: "rev", fmt: (v) => "₹" + (v / 1000).toFixed(1) + "k", label: "Revenue" },
    occ: { key: "occ", fmt: (v) => v + "%", label: "Occupancy" },
    bookings: { key: "bookings", fmt: (v) => v, label: "Bookings" },
  }[metric];

  if (loading) {
    return (
      <div className="page fade-in">
        <div className="row g-3 mb-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="col-12 col-sm-6 col-lg-3">
              <div className="card card-pad" style={{ height: 120 }}>
                <div style={{ height: 16, background: 'var(--border)', borderRadius: 4, marginBottom: 12 }} />
                <div style={{ height: 28, background: 'var(--border)', borderRadius: 4, marginBottom: 12 }} />
                <div style={{ height: 12, background: 'var(--border)', borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-8">
            <div className="card" style={{ height: 280 }}>
              <div style={{ padding: 16, background: 'var(--surface)' }}>
                <div style={{ height: 20, background: 'var(--border)', borderRadius: 4 }} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="card" style={{ height: 280 }}>
              <div style={{ padding: 16 }}>
                <SkeletonRows rows={4} cols={1} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page fade-in" role="main" aria-label="Host dashboard">
      {/* 3-Column Layout: KPIs | Today's Schedule | Key Metrics */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', minHeight: '420px' }}>

        {/* COLUMN 1: KPI Cards Stacked Vertically - LARGER */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1.2, minWidth: 0 }}>
          <Kpi label="Bookings today" icon={<HI.calendar s={15} />} value={userBookings.filter(b => b.date === 'Today').length}
            footer={<><span className="muted">{checkedIn} checked in · {userBookings.filter(b => b.date === 'Today').length - checkedIn} to come</span></>} />
          <Kpi label="Revenue today" icon={<HI.wallet s={15} />} value={money(todayRev)}
            footer={<><Delta value={Math.floor(Math.random() * 20)} /><span className="muted">vs last Tue</span></>} />
          <Kpi label="Occupancy" icon={<HI.seat s={15} />} value={occ + "%"}
            footer={<><Delta value={Math.floor(Math.random() * 10)} /><span className="muted">desk-hours filled</span></>} />
          <Kpi label="Guest Rating" icon={<HI.star s={15} />} value="4.8★"
            footer={<><span className="muted">0.1 away from 5-star</span></>} />
        </div>

        {/* COLUMN 2: Today's Schedule - LARGER */}
        <div style={{ flex: 1.3, minWidth: 0 }}>
          <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHead title="Today's schedule" sub={H.today.length + " bookings"}
            right={<button className="btn btn-quiet btn-sm" onClick={() => go("bookings")} aria-label="View all bookings">View all</button>} />
            <div className="card-pad" style={{ flex: 1, overflowY: "auto", padding: '6px' }}>
              <TodaySchedule openBooking={openBooking} bookings={userBookings} />
            </div>
          </div>
        </div>

        {/* COLUMN 3: Key Metrics - COMPACT */}
        <div style={{ flex: 0.8, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-2)', paddingLeft: '4px', marginBottom: '0px' }}>Metrics</div>

          {/* Revenue Growth */}
          <div className="card" style={{ flex: 1, padding: '8px 6px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600 }}>Growth</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--green)', marginTop: '2px' }}>↑ 15%</div>
            <div style={{ fontSize: '8px', color: 'var(--muted)', marginTop: '2px' }}>vs last week</div>
          </div>

          {/* Repeat Guest Rate */}
          <div className="card" style={{ flex: 1, padding: '8px 6px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600 }}>Repeat</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--amber)', marginTop: '2px' }}>68%</div>
            <div style={{ fontSize: '8px', color: 'var(--muted)', marginTop: '2px' }}>returning</div>
          </div>

          {/* Rating Progress */}
          <div className="card" style={{ flex: 1, padding: '8px 6px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600 }}>Rating</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--clay)', marginTop: '2px' }}>4.8★</div>
            <div style={{ fontSize: '8px', color: 'var(--muted)', marginTop: '2px' }}>to 5-star</div>
          </div>
        </div>
      </div>

      {/* INSIGHTS SECTION - MOVED BEFORE ANALYTICS */}
      <div className="row between" style={{ marginBottom: 12, width: "100%", boxSizing: "border-box" }}>
        <div className="t-h2 nowrap">Insights for you</div>
        <span className="t-xs muted">Updated daily from your booking data</span>
      </div>
      <div className="insights-grid" style={{ marginBottom: 20 }}>
        {H.insights.map((ins, i) => <InsightCard key={i} ins={ins} go={go} toast={toast} />)}
      </div>

      {/* chart + week overview */}
      <div className="row g-3 mb-4" aria-label="Weekly analytics">
        <div className="col-12">
          <div className="card">
            <CardHead title="This week" sub="Mon–Sun · resets Sunday midnight"
            right={<div className="seg">
              {Object.keys(metricCfg ? { rev: 1, occ: 1, bookings: 1 } : {}).length ? null : null}
              {["rev", "occ", "bookings"].map((m) => (
                <button key={m} className={metric === m ? "on" : ""} onClick={() => setMetric(m)}>
                  {m === "rev" ? "Revenue" : m === "occ" ? "Occupancy" : "Bookings"}
                </button>
              ))}
            </div>} />
          <div className="card-pad">
            <BarChart data={H.week} valueKey={metricCfg.key} height={170}
              format={metric === "rev" ? (v) => "₹" + (v / 1000).toFixed(1) + "k" : metric === "occ" ? (v) => v + "%" : (v) => v} />
          </div>
          <div className="card-foot row between">
            <span className="t-sm muted">Best day · <strong className="ink2">Saturday</strong> ({metric === "rev" ? money(3880) : metric === "occ" ? "96%" : "12 bookings"})</span>
            <button className="btn btn-sm btn-ghost" onClick={() => go("analytics")}>Full analytics</button>
          </div>
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-6">
          <div className="card">
            <CardHead title="Peak booking hours" sub="When your desks are busiest" />
          <div className="card-pad col g14">
            {[
              { time: '10am–12pm', bookings: '8', percent: 85 },
              { time: '2pm–4pm', bookings: '6', percent: 72 },
              { time: '9am–10am', bookings: '5', percent: 58 },
              { time: '1pm–2pm', bookings: '4', percent: 45 }
            ].map((slot, i) => (
              <div key={i}>
                <div className="row between" style={{ marginBottom: 6, alignItems: "center" }}>
                  <div className="t-sm" style={{ fontWeight: 600 }}>{slot.time}</div>
                  <span className="t-xs muted">{slot.bookings} bookings</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--green)', width: slot.percent + '%' }} />
                  </div>
                  <span className="mono t-xs" style={{ fontWeight: 600, minWidth: 24 }}>{slot.percent}%</span>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card">
            <CardHead title="Popular session lengths" sub="Share of bookings by duration" />
          <div className="card-pad col g12">
            {[
              { label: '2 hours', percent: 42, color: 'var(--green)' },
              { label: '4 hours', percent: 28, color: 'var(--green-ink)' },
              { label: '1 hour', percent: 18, color: 'var(--amber)' },
              { label: '8+ hours', percent: 12, color: 'var(--clay)' }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 80 }}>
                  <div className="t-sm" style={{ fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
                  <div className="t-xs muted">{item.percent}%</div>
                </div>
                <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: item.color, width: item.percent + '%' }} />
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
